import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, CatalogApi } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { monitorApiRef, MonitorApi } from '@internal/backstage-plugin-ewii-azure';
import { ServiceRow, getSystemTitles, processComponent } from '../utils';

export const useReleaseData = () => {
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [catalogEntities, setCatalogEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const catalogApi = useApi(catalogApiRef) as CatalogApi;
  const monitorApi = useApi(monitorApiRef) as MonitorApi;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [{ items: components }, { items: systems }, healthStatus] = await Promise.all([
        catalogApi.getEntities({ filter: { kind: 'Component' } }),
        catalogApi.getEntities({ filter: { kind: 'System' } }),
        monitorApi.getHealthStatus()
      ]);

      const envSet = new Set<string>();
      const services: Record<string, ServiceRow> = {};
      const systemTitles = getSystemTitles(systems as Entity[]);
      
      (components as Entity[]).forEach(entity => 
        processComponent(entity, services, systemTitles, envSet, healthStatus)
      );

      setEnvironments(Array.from(envSet));
      setServices(Object.values(services).sort((a, b) => a.serviceName.localeCompare(b.serviceName)));
      setCatalogEntities(components as Entity[]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error instanceof Error ? error : new Error('Failed to fetch service data'));
    } finally {
      setLoading(false);
    }
  }, [catalogApi, monitorApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { services, environments, catalogEntities, loading, error, refetch: fetchData };
}; 